import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchMindfulnessCatalog,
  toggleExerciseFavouriteApi,
  completeExerciseApi,
} from "../services/mindfulnessService";

/**
 * Async Thunk to fetch mindfulness catalog
 * Accepts { silent: boolean }. When silent is true (e.g. background revalidation),
 * loading remains false so the UI does not flicker or show a loader.
 */
export const fetchMindfulnessExercisesThunk = createAsyncThunk(
  "mindfulness/fetchExercises",
  async ({ silent = false } = {}, { rejectWithValue }) => {
    try {
      const data = await fetchMindfulnessCatalog();
      return { ...data, silent };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch exercises");
    }
  }
);

/**
 * Optimistic Async Thunk to toggle exercise favourite status.
 * Immediately updates Redux store for 0-delay UI feedback.
 * If the backend API call fails, rolls back Redux store to the previous state.
 */
export const toggleFavouriteThunk = createAsyncThunk(
  "mindfulness/toggleFavourite",
  async ({ title, slug, exerciseId, isFavourited }, { dispatch, rejectWithValue }) => {
    // 1. Optimistically update Redux store immediately
    dispatch(updateFavouriteLocally({ title, slug, exerciseId, isFavourited }));

    try {
      const response = await toggleExerciseFavouriteApi(title || slug, isFavourited);
      return { title, slug, exerciseId, isFavourited, result: response?.data };
    } catch (err) {
      // 2. Rollback Redux store if the backend call fails
      dispatch(updateFavouriteLocally({ title, slug, exerciseId, isFavourited: !isFavourited }));
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Failed to update favourite status"
      );
    }
  }
);

/**
 * Optimistic Async Thunk to complete an exercise.
 * Immediately updates Redux store for 0-delay UI feedback.
 * If the backend API call fails, rolls back Redux store to the previous state.
 */
export const completeExerciseThunk = createAsyncThunk(
  "mindfulness/completeExercise",
  async (
    { exerciseId, slug, title, duration = 0 },
    { dispatch, rejectWithValue }
  ) => {
    // 1. Optimistically mark completed in Redux store immediately
    dispatch(
      updateExerciseCompletionLocally({
        exerciseId,
        slug,
        title,
        isCompleted: true,
      })
    );

    try {
      const response = await completeExerciseApi(
        slug || exerciseId || title,
        duration
      );
      return { exerciseId, slug, title, duration, result: response?.data };
    } catch (err) {
      // 2. Rollback Redux store if the backend call fails
      dispatch(
        updateExerciseCompletionLocally({
          exerciseId,
          slug,
          title,
          isCompleted: false,
        })
      );
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to complete exercise"
      );
    }
  }
);

const initialState = {
  exercises: [],
  mindfulnessCategory: {},
  userFavourites: [],
  loading: false,         // True ONLY on initial load before any data is cached
  isRevalidating: false,  // True when updating silently in background
  initialLoaded: false,   // True once exercises have been fetched at least once
  lastFetched: null,
  error: null,
};

const mindfulnessSlice = createSlice({
  name: "mindfulness",
  initialState,
  reducers: {
    setExercises(state, action) {
      state.exercises = action.payload || [];
    },
    updateExerciseCompletionLocally(state, action) {
      const { exerciseId, slug, title, isCompleted = true } = action.payload || {};
      const targetId = exerciseId ? Number(exerciseId) : null;
      const targetSlug = slug ? String(slug).toLowerCase() : null;
      const targetTitle = title ? String(title).trim().toLowerCase() : null;

      state.exercises = state.exercises.map((ex) => {
        const matchId = targetId && Number(ex.exercise_id || ex.id) === targetId;
        const matchSlug =
          targetSlug && String(ex.slug || ex.defaulturl).toLowerCase() === targetSlug;
        const matchTitle =
          targetTitle && String(ex.title).trim().toLowerCase() === targetTitle;

        if (matchId || matchSlug || matchTitle) {
          return {
            ...ex,
            is_completed: isCompleted,
          };
        }
        return ex;
      });
    },
    updateFavouriteLocally(state, action) {
      const { title, slug, exerciseId, isFavourited } = action.payload || {};
      const key = title ? String(title).trim().toLowerCase() : "";
      const targetSlug = slug ? String(slug).trim().toLowerCase() : "";
      const targetId = exerciseId ? Number(exerciseId) : null;

      if (key) {
        if (isFavourited) {
          if (!state.userFavourites.includes(key)) {
            state.userFavourites.push(key);
          }
        } else {
          state.userFavourites = state.userFavourites.filter((f) => f !== key);
        }
      }

      state.exercises = state.exercises.map((ex) => {
        const matchTitle = key && String(ex.title || "").trim().toLowerCase() === key;
        const matchSlug = targetSlug && (
          String(ex.slug || "").toLowerCase() === targetSlug || 
          String(ex.defaulturl || "").toLowerCase() === targetSlug
        );
        const matchId = targetId && Number(ex.exercise_id || ex.id) === targetId;

        if (matchTitle || matchSlug || matchId) {
          return { ...ex, is_favourited: Boolean(isFavourited) };
        }
        return ex;
      });
    },
    resetMindfulness(state) {
      state.exercises = [];
      state.mindfulnessCategory = {};
      state.userFavourites = [];
      state.loading = false;
      state.isRevalidating = false;
      state.initialLoaded = false;
      state.lastFetched = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMindfulnessExercisesThunk.pending, (state, action) => {
        const isSilent = action.meta.arg?.silent;
        if (state.initialLoaded || isSilent) {
          state.isRevalidating = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMindfulnessExercisesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isRevalidating = false;
        state.initialLoaded = true;
        state.exercises = action.payload.exercises;
        state.mindfulnessCategory = action.payload.mindfulnessCategory;
        state.userFavourites = action.payload.userFavourites;
        state.lastFetched = Date.now();
      })
      .addCase(fetchMindfulnessExercisesThunk.rejected, (state, action) => {
        state.loading = false;
        state.isRevalidating = false;
        state.error = action.payload || "Failed to load mindfulness practices";
      });
  },
});

export const {
  setExercises,
  updateExerciseCompletionLocally,
  updateFavouriteLocally,
  resetMindfulness,
} = mindfulnessSlice.actions;

export default mindfulnessSlice.reducer;

// Selectors
export const selectMindfulnessExercises = (state) => state.mindfulness.exercises;
export const selectMindfulnessCategories = (state) => state.mindfulness.mindfulnessCategory;
export const selectMindfulnessFavourites = (state) => state.mindfulness.userFavourites;
export const selectMindfulnessLoading = (state) => state.mindfulness.loading;
export const selectMindfulnessIsRevalidating = (state) => state.mindfulness.isRevalidating;
export const selectMindfulnessInitialLoaded = (state) => state.mindfulness.initialLoaded;
export const selectMindfulnessLastFetched = (state) => state.mindfulness.lastFetched;
